/**
 * EGCore - an electric guitar that is MODELLED, not sampled.
 *
 * WHY A MODEL
 * -----------
 * Everything a rock guitarist does with the left hand after the pick has hit
 * the string - bend it, shake it, slide it, hammer on, pull off, choke it
 * with the palm, touch a harmonic - changes the string that is ALREADY
 * vibrating. A sample player can only ever start a recording, so every
 * browser "virtual electric guitar" we measured either cannot do these at all
 * or fakes a bend by speeding the recording up. Here the string is a digital
 * waveguide (Karplus-Strong, extended as in Jaffe & Smith 1983 and
 * Karjalainen, Valimaki & Tolonen 1998): the energy lives in a delay line one
 * period long, and a bend is literally the line getting shorter while the
 * energy in it keeps going.
 *
 * It also costs nothing to download, and every tuning, every guitar and every
 * pickup position is the same arithmetic with different numbers.
 *
 * WHAT IS PHYSICAL HERE (and therefore measurable against a real DI)
 * -----------------------------------------------------------------
 *   excitation    a pick releases the string at a distance d_pick from the
 *                 bridge. At the pickup that is a rectangular velocity pulse
 *                 2*d_pick/c long (c = wave speed), with its edges rounded by
 *                 how hard the pick is. Its spectrum is sin(k*pi*beta)/k -
 *                 the textbook plucked string - without a table of harmonics.
 *   loss          one-pole loop filter designed from TWO decay times, at the
 *                 fundamental and high up, i.e. from sigma(f) = s0 + s2*f^2
 *                 (Chaigne & Askenfelt), not from magic coefficients. That is
 *                 what lets a palm mute, a released note or a harmonic be the
 *                 same string with a different number.
 *   pickup        a magnetic pickup at distance d from the bridge hears the
 *                 string through a comb 1 - z^-(2d/c); a humbucker is two
 *                 coils, i.e. two combs averaged. The delay depends on the
 *                 WAVE SPEED only, so it is constant per string whatever the
 *                 fret - which is exactly why a bridge pickup gets relatively
 *                 darker up the neck on a real guitar, and does here too.
 *   polarisations two loops a hair apart in length and loss: the horizontal
 *                 and vertical vibration of a real string, whose beating is
 *                 the "alive" sustain a single loop never has.
 *   stiffness     the upper partials run sharp of the harmonic series
 *                 (inharmonicity B, measured per string): allpass sections
 *                 in the loop.
 *   tension       a pick pulls the pitch sharp and it sinks back as the
 *                 string calms down (tension modulation): 6-20 cents,
 *                 gone in about a second - measured on real DI notes.
 *
 * The pickup's electrical resonance (L with the cable C) and the tone knob are
 * NOT here: they are an ordinary filter and live on the page as native nodes,
 * where they are automatable and cost nothing.
 *
 * PURITY
 * ------
 * No DOM, no Web Audio, no timers. The same file runs in the AudioWorklet
 * (eg-worklet.js), on the page for the offline export render, and in Node for
 * the measurements - the thing measured is the thing that plays.
 */
(function (root, factory) {
    var api = factory();
    if (typeof globalThis !== 'undefined') globalThis.EGCore = api;
    if (typeof window !== 'undefined') window.EGCore = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
}(this, function () {
    'use strict';

    var LN1000 = Math.log(1000);

    function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
    function midiToHz(m) { return 440 * Math.pow(2, (m - 69) / 12); }

    /* mulberry32: seeded, so a take re-renders identically on export. */
    function rng(seed) {
        var a = seed | 0;
        return function () {
            a = (a + 0x6D2B79F5) | 0;
            var t = Math.imul(a ^ (a >>> 15), 1 | a);
            t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
            return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
        };
    }

    // ───────────────────────────── guitars ─────────────────────────────────
    /*
     * Geometry is in millimetres from the bridge saddle to the coil centre.
     * A single coil lists [treble side, bass side] (slanted pickups) and the
     * model interpolates across the six strings; a humbucker (`hb`) lists
     * its two coils, heard as two combs averaged.
     * `res` is the pickup's loaded electrical resonance {f, q} - the page
     * builds the filter from it; `out` is relative output (a hot humbucker
     * drives the amp harder, which is most of why it sounds "heavier").
     *
     * Sources for the numbers: manufacturer routing templates (scale, pickup
     * rout positions) and published pickup measurements (inductance with a
     * typical 3 m cable -> peak frequency). They are starting points; the
     * calibration against real DI recordings is in tests/audio.
     */
    var MODELS = {
        s: {                        // three single coils, 25.5" - glassy, quacky in 2/4
            scale: 648, frets: 22,
            coils: {
                n: { d: [160, 160], ap: 12 },
                m: { d: [99, 99], ap: 12 },
                b: { d: [38, 50], ap: 12 }
            },
            /* Resonances FITTED to the attack spectra of a real Strat's five
               positions (EGFxSet DI, least squares over 0.3-4.5 kHz after the
               string model was calibrated): bridge 2.7 kHz Q 1.6, middle and
               neck ~3.3 kHz Q 2.3, the parallel 2/4 positions ~4.6 kHz (two
               coils in parallel halve the inductance; their fitted Q ran to the
               grid edge and is capped). */
            sel: {
                n: { c: [['n', 1]], res: { f: 3300, q: 2.2 }, out: 1.0 },
                nm: { c: [['n', 0.5], ['m', 0.5]], res: { f: 4600, q: 2.4 }, out: 0.95 },
                m: { c: [['m', 1]], res: { f: 3350, q: 2.2 }, out: 1.0 },
                mb: { c: [['m', 0.5], ['b', 0.5]], res: { f: 4600, q: 2.0 }, out: 0.95 },
                b: { c: [['b', 1]], res: { f: 2700, q: 1.6 }, out: 1.05 }
            },
            order: ['n', 'nm', 'm', 'mb', 'b'], def: 'b'
        },
        t: {                        // two single coils, bridge coil very close = twang
            scale: 648, frets: 21,
            coils: {
                n: { d: [155, 155], ap: 10 },
                b: { d: [30, 42], ap: 13 }
            },
            sel: {
                n: { c: [['n', 1]], res: { f: 2900, q: 1.4 }, out: 0.9 },
                nb: { c: [['n', 0.5], ['b', 0.5]], res: { f: 4100, q: 2.0 }, out: 0.95 },
                b: { c: [['b', 1]], res: { f: 3400, q: 2.3 }, out: 1.15 }
            },
            order: ['n', 'nb', 'b'], def: 'b'
        },
        lp: {                       // two humbuckers, 24.75" - thick, the classic-rock/hard-rock guitar
            scale: 628, frets: 22, hum: 0.12,
            coils: {
                n: { d: [148, 166], ap: 14, hb: true },
                b: { d: [29, 47], ap: 14, hb: true }
            },
            sel: {
                n: { c: [['n', 1]], res: { f: 2500, q: 1.9 }, out: 1.55 },
                nb: { c: [['n', 0.5], ['b', 0.5]], res: { f: 2900, q: 1.5 }, out: 1.5 },
                b: { c: [['b', 1]], res: { f: 2800, q: 2.1 }, out: 1.7 }
            },
            order: ['n', 'nb', 'b'], def: 'b'
        },
        ss: {                       // superstrat: hot bridge humbucker, 24 frets - hard rock, metal
            scale: 648, frets: 24, hum: 0.15,
            coils: {
                n: { d: [143, 161], ap: 14, hb: true },
                m: { d: [99, 99], ap: 12 },
                b: { d: [31, 49], ap: 14, hb: true }
            },
            sel: {
                n: { c: [['n', 1]], res: { f: 2600, q: 1.7 }, out: 1.8 },
                m: { c: [['m', 1]], res: { f: 4200, q: 2.2 }, out: 1.0 },
                b: { c: [['b', 1]], res: { f: 3100, q: 1.4 }, out: 2.3 }
            },
            order: ['n', 'm', 'b'], def: 'b'
        },
        s7: {                       // seven strings, 26.5", two hot humbuckers - djent, nu-metal
            scale: 673, frets: 24, strings: 7, hum: 0.12,
            coils: {
                n: { d: [146, 164], ap: 15, hb: true },
                b: { d: [32, 50], ap: 15, hb: true }
            },
            sel: {
                n: { c: [['n', 1]], res: { f: 2700, q: 1.5 }, out: 1.9 },
                nb: { c: [['n', 0.5], ['b', 0.5]], res: { f: 3000, q: 1.3 }, out: 1.8 },
                b: { c: [['b', 1]], res: { f: 3300, q: 1.3 }, out: 2.4 }
            },
            order: ['n', 'nb', 'b'], def: 'b'
        },
        bass: {                     // 34" four-string bass with a split-coil pickup - the jam band
            scale: 864, frets: 20, strings: 4, hum: 0.2,
            coils: { p: { d: [150, 170], ap: 20, hb: true } },
            sel: { p: { c: [['p', 1]], res: { f: 1900, q: 1.1 }, out: 1.6 } },
            order: ['p'], def: 'p',
            damp: [
                { smin: 0.30, b: 0.0030, open: 0.4 },
                { smin: 0.28, b: 0.0034, open: 0.4 },
                { smin: 0.26, b: 0.0038, open: 0.4 },
                { smin: 0.24, b: 0.0042, open: 0.4 }
            ]
        },
        o: {                        // offset: wide flat single coils - surf, indie, shoegaze
            scale: 648, frets: 21,
            coils: {
                n: { d: [155, 155], ap: 24 },
                b: { d: [44, 44], ap: 24 }
            },
            sel: {
                n: { c: [['n', 1]], res: { f: 3300, q: 1.7 }, out: 1.1 },
                nb: { c: [['n', 0.5], ['b', 0.5]], res: { f: 3800, q: 1.4 }, out: 1.05 },
                b: { c: [['b', 1]], res: { f: 3600, q: 1.9 }, out: 1.15 }
            },
            order: ['n', 'nb', 'b'], def: 'b'
        }
    };

    /* Open-string MIDI notes, index 0 = string 1 (high E) ... 5 = string 6.
       Seven-string tunings add the low string at index 6. */
    var TUNINGS = {
        std: [64, 59, 55, 50, 45, 40],     // E A D G B E
        eb: [63, 58, 54, 49, 44, 39],      // half step down
        dropd: [64, 59, 55, 50, 45, 38],
        dstd: [62, 57, 53, 48, 43, 38],
        dropcs: [63, 58, 54, 49, 44, 37],
        dropc: [62, 57, 53, 48, 43, 36],
        cstd: [60, 55, 51, 46, 41, 36],
        dropb: [61, 56, 52, 47, 42, 35],
        bstd: [59, 54, 50, 45, 40, 35],
        openg: [62, 59, 55, 50, 43, 38],   // D G D G B D
        opend: [62, 57, 54, 50, 45, 38],   // D A D F# A D
        opene: [64, 59, 56, 52, 47, 40],   // E B E G# B E
        dadgad: [62, 57, 55, 50, 45, 38],
        bass4: [43, 38, 33, 28],           // G D A E (bass)
        b7: [64, 59, 55, 50, 45, 40, 35],  // 7-string B standard
        a7: [64, 59, 55, 50, 45, 40, 33],  // 7-string drop A
        a7std: [62, 57, 53, 48, 43, 38, 33] // 7-string A standard (tuned down a tone)
    };

    /*
     * Per-string damping, index 0 = high E: the decay RATE of a partial,
     * sigma(f) = smin + b*f (1/s), with T60 = 6.91 / sigma.
     *
     * MEASURED, not chosen: 690 DI notes of a real Stratocaster (EGFxSet,
     * CC BY 4.0), every partial above -30 dB tracked over 2.8 s and fitted by
     * frequency band (tests/audio measurement, 2026-09-25). Two findings drove
     * this table. Real strings keep their top end far longer than a
     * Karplus-Strong default: at 1.0-1.6 s the 3 kHz band of a real note is
     * ~30 dB louder than our first model's. And the rate grows about
     * LINEARLY with frequency, not quadratically - which is why the loop
     * filter below is a three-point pole-zero fit and not a one-pole.
     * `open` scales smin for an open string: an unfretted wound string's
     * fundamental barely decays at all (T60 40-100 s measured).
     */
    var DAMP = [
        { smin: 0.55, b: 0.00062, open: 0.8 },
        { smin: 0.35, b: 0.0007, open: 0.8 },
        { smin: 0.45, b: 0.0008, open: 0.75 },
        { smin: 0.30, b: 0.0013, open: 0.3 },
        { smin: 0.25, b: 0.0017, open: 0.3 },
        { smin: 0.20, b: 0.0023, open: 0.3 },
        { smin: 0.18, b: 0.0027, open: 0.3 }
    ];
    /* A fretted note loses energy into the finger and the fret: the measured
       rate grows ~4 % per fret. */
    var FRET_LOSS = 0.04;
    /* Relative output per string (pole piece stagger + string mass). */
    var STRING_OUT = [0.85, 0.9, 1.0, 1.05, 1.05, 1.0, 0.95];

    // ─────────────────────────── loss filter ──────────────────────────────
    /*
     * H(z) = g (1 + c z^-1) / (1 + a z^-1), fitted EXACTLY to the wanted
     * per-period gains at three frequencies (the fundamental, ~900 Hz and
     * ~3 kHz). With |H|^2 = g^2 (1 + 2c cos w + c^2) / (1 + 2a cos w + a^2)
     * the three equations are linear in (g^2(1+c^2), 2g^2c) once 2a is fixed,
     * so eliminating those leaves a quadratic in 2a - closed form, cheap
     * enough to redo every audio block while a note bends.
     * Falls back to the two-point one-pole if the three points do not admit a
     * stable, passive solution (e.g. very high notes where the points crowd).
     * Returns the phase delay at w0 so the delay line keeps the note in tune.
     */
    function designLoss1(G0, G1, w0, w1, o) {
        G0 = clamp(G0, 1e-6, 0.99999);
        G1 = clamp(G1, 1e-7, G0 * 0.99999);
        var R2 = (G1 / G0) * (G1 / G0);
        var c0 = Math.cos(w0), c1 = Math.cos(w1);
        var A = R2 - 1, B = 2 * (R2 * c1 - c0);
        var disc = B * B - 4 * A * A, a = 0;
        if (disc >= 0 && Math.abs(A) > 1e-12) {
            var sq = Math.sqrt(disc);
            var r1 = (-B + sq) / (2 * A), r2 = (-B - sq) / (2 * A);
            if (r1 > -1 && r1 <= 0) a = r1; else if (r2 > -1 && r2 <= 0) a = r2;
            else a = clamp(Math.abs(r1) < Math.abs(r2) ? r1 : r2, -0.999, 0);
        }
        a = clamp(a, -0.999, 0);
        var g = G0 * Math.sqrt(1 + 2 * a * c0 + a * a) / (1 + a);
        if (g > 0.99999) g = 0.99999;
        o.g = g * (1 + a); o.c = 0; o.a = a;
        var ph = Math.atan2(a * Math.sin(w0), 1 + a * Math.cos(w0));
        o.tau = w0 > 1e-9 ? -ph / w0 : 0;
        return o;
    }
    function designLoss(Gs, ws, o) {
        o = o || {};
        var u0 = Math.cos(ws[0]), u1 = Math.cos(ws[1]), u2 = Math.cos(ws[2]);
        var M0 = Gs[0] * Gs[0], M1 = Gs[1] * Gs[1], M2 = Gs[2] * Gs[2];
        function coef(Ma, ua, Mb, ub) {
            var du = ua - ub;
            return [(Ma - Mb) / 4 / du, (Ma * ua - Mb * ub) / du, (Ma - Mb) / du];
        }
        var k1 = coef(M0, u0, M1, u1), k2 = coef(M0, u0, M2, u2);
        var q2 = k1[0] - k2[0], q1 = k1[1] - k2[1], q0 = k1[2] - k2[2];
        var roots = [];
        if (Math.abs(q2) > 1e-18) {
            var d = q1 * q1 - 4 * q2 * q0;
            if (d >= 0) { var sd = Math.sqrt(d); roots.push((-q1 + sd) / (2 * q2), (-q1 - sd) / (2 * q2)); }
        } else if (Math.abs(q1) > 1e-18) roots.push(-q0 / q1);
        for (var i = 0; i < roots.length; i++) {
            var Bq = roots[i], a = Bq / 2;
            if (!(a > -0.995 && a < 0.995)) continue;
            var D = k1[0] * Bq * Bq + k1[1] * Bq + k1[2];
            var C = M0 * (1 + Bq * Bq / 4) + M0 * u0 * Bq - D * u0;
            if (!(C > 0)) continue;
            var kk = D / (2 * C);
            if (Math.abs(kk) >= 0.5) continue;
            var c = Math.abs(kk) > 1e-12 ? (1 - Math.sqrt(1 - 4 * kk * kk)) / (2 * kk) : 0;
            var g = Math.sqrt(C / (1 + c * c));
            // passive at DC and at Nyquist, or the string would ring forever
            var dcg = g * (1 + c) / (1 + a), nyq = g * Math.abs(1 - c) / Math.abs(1 - a);
            if (!(dcg < 0.99999 && nyq < 0.99999 && dcg > 0)) continue;
            o.g = g; o.c = c; o.a = a;
            var w0 = ws[0];
            var ph = Math.atan2(-c * Math.sin(w0), 1 + c * Math.cos(w0)) - Math.atan2(-a * Math.sin(w0), 1 + a * Math.cos(w0));
            o.tau = w0 > 1e-9 ? -ph / w0 : 0;
            return o;
        }
        return designLoss1(Gs[0], Gs[2], ws[0], ws[2], o);
    }

    // ───────────────────────────── one slot ───────────────────────────────
    /*
     * A slot is one vibrating note: two polarisation loops sharing one output
     * history (for the pickup comb taps). Two slots per string let a re-picked
     * string cross-fade in a few milliseconds - the pick touching a ringing
     * string - instead of clicking.
     */
    var LEN = 4096;                  // power of two, > longest period + margin
    var MASK = LEN - 1;

    /*
     * Stiffness. A real string is a little stiff, so its upper partials run
     * sharp of the harmonic series: f_k = k f0 sqrt(1 + B k^2). Measured on
     * EGFxSet DI notes (6 strings x 3 pickups x frets 0-12): B ~ 1.6-1.9e-4
     * on the wound strings, 1.5e-4 on G, 4e-5 on B, ~8e-5 on high e, growing
     * as 1/L^2 up the neck - the 13th partial of an open low E is 23 cents
     * sharp, the 25th a whole semitone. The first model was harmonic to
     * 1-3 cents: through distortion a harmonic source makes products that all
     * land on the same series, the buzz of a synth; a stiff one makes the
     * grit of a guitar. The loop gets DISP_M first-order allpass sections
     * with one coefficient, taken from a table fitted offline to the partial
     * cents up to ~2.5 kHz by RELATIVE error: four sections cannot follow
     * B k^2 on a low string all the way up, and an absolute fit over-stretched
     * the strong low partials to chase the weak high ones (low E partial 5 at
     * +9 cents against a measured +3). tests/scratch/eg/disp-table-scratch.py
     */
    var B_OPEN = [8e-5, 4e-5, 1.5e-4, 1.6e-4, 1.6e-4, 1.9e-4, 2.2e-4];   // high e ... low E, low B
    var B_BASS = [1.5e-4, 1.6e-4, 1.8e-4, 2.0e-4];                        // G D A E
    var DISP_M = 4;
    var DISP_F0 = 40, DISP_F1 = 1400, DISP_B0 = 1e-5, DISP_B1 = 4e-3;
    var DISP_A = [
            [-.802, -.828, -.848, -.868, -.882, -.897, -.910, -.922, -.930, -.930, -.930, -.930, -.930, -.930, -.930, -.930],
            [-.770, -.797, -.823, -.845, -.863, -.880, -.895, -.910, -.920, -.930, -.930, -.930, -.930, -.930, -.930, -.930],
            [-.735, -.765, -.792, -.818, -.840, -.860, -.877, -.892, -.907, -.917, -.930, -.930, -.930, -.930, -.930, -.930],
            [-.695, -.728, -.760, -.787, -.815, -.838, -.858, -.875, -.890, -.905, -.917, -.927, -.930, -.930, -.930, -.930],
            [-.650, -.688, -.723, -.755, -.782, -.810, -.833, -.853, -.873, -.887, -.902, -.915, -.925, -.930, -.930, -.930],
            [-.598, -.640, -.677, -.715, -.748, -.777, -.802, -.828, -.848, -.868, -.885, -.897, -.912, -.922, -.930, -.930],
            [-.540, -.585, -.627, -.667, -.705, -.738, -.767, -.795, -.820, -.843, -.863, -.880, -.895, -.907, -.920, -.930],
            [-.480, -.527, -.573, -.615, -.655, -.693, -.728, -.760, -.787, -.812, -.835, -.858, -.875, -.890, -.905, -.915],
            [-.417, -.465, -.512, -.560, -.603, -.645, -.682, -.718, -.750, -.780, -.805, -.830, -.850, -.870, -.885, -.900],
            [-.352, -.403, -.453, -.500, -.547, -.593, -.635, -.672, -.710, -.743, -.772, -.800, -.823, -.845, -.865, -.882],
            [-.290, -.340, -.390, -.438, -.487, -.535, -.580, -.623, -.662, -.698, -.733, -.762, -.790, -.815, -.838, -.858],
            [-.233, -.278, -.328, -.375, -.425, -.475, -.522, -.568, -.613, -.652, -.690, -.725, -.755, -.785, -.810, -.833],
            [-.177, -.220, -.265, -.312, -.362, -.412, -.460, -.510, -.555, -.600, -.640, -.677, -.715, -.745, -.775, -.802],
            [-.130, -.168, -.207, -.253, -.300, -.347, -.398, -.448, -.497, -.542, -.588, -.630, -.667, -.705, -.738, -.767],
            [-.092, -.122, -.158, -.198, -.240, -.287, -.335, -.385, -.435, -.482, -.530, -.575, -.618, -.657, -.695, -.730],
            [-.062, -.085, -.115, -.147, -.185, -.228, -.275, -.323, -.372, -.422, -.472, -.520, -.565, -.610, -.650, -.688],
            [-.043, -.058, -.080, -.105, -.138, -.175, -.217, -.263, -.312, -.360, -.412, -.460, -.510, -.557, -.600, -.642],
            [-.028, -.037, -.052, -.072, -.100, -.130, -.165, -.207, -.253, -.300, -.350, -.400, -.450, -.500, -.547, -.593],
            [-.018, -.025, -.035, -.050, -.068, -.092, -.122, -.158, -.198, -.240, -.287, -.338, -.390, -.440, -.490, -.537],
            [-.010, -.015, -.022, -.033, -.045, -.062, -.085, -.115, -.147, -.188, -.230, -.278, -.328, -.378, -.430, -.480],
            [-.007, -.010, -.015, -.020, -.030, -.043, -.058, -.080, -.107, -.140, -.177, -.220, -.268, -.318, -.367, -.420],
            [-.005, -.005, -.007, -.013, -.018, -.028, -.037, -.052, -.075, -.100, -.133, -.168, -.210, -.258, -.305, -.357]
    ];
    function dispCoef(f1, B) {
        var nf = DISP_A.length, nb = DISP_A[0].length;
        var x = Math.log(clamp(f1, DISP_F0, DISP_F1) / DISP_F0) / Math.log(DISP_F1 / DISP_F0) * (nf - 1);
        var y = Math.log(clamp(B, DISP_B0, DISP_B1) / DISP_B0) / Math.log(DISP_B1 / DISP_B0) * (nb - 1);
        var i = Math.min(nf - 2, x | 0), j = Math.min(nb - 2, y | 0), fx = x - i, fy = y - j;
        var r0 = DISP_A[i], r1 = DISP_A[i + 1];
        return (r0[j] * (1 - fy) + r0[j + 1] * fy) * (1 - fx) + (r1[j] * (1 - fy) + r1[j + 1] * fy) * fx;
    }
    /* phase delay (samples) of one section (a + z^-1)/(1 + a z^-1) at w */
    function apTau(w, a) {
        var s = Math.sin(w), c = Math.cos(w);
        return -(Math.atan2(-s, a + c) - Math.atan2(-a * s, 1 + a * c)) / w;
    }

    function Slot(sr) {
        this.sr = sr;
        this.a = new Float32Array(LEN);
        this.b = new Float32Array(LEN);
        this.h = new Float32Array(LEN);    // output history for pickup taps
        this.w = 0;
        this.active = false;
        this.gain = 1; this.gainTarget = 1; this.gainStep = 0;
        this.exc = new Float32Array(2048);
        this.excLen = 0; this.excPos = 0; this.excStart = 0;
        this.sA = 0; this.sB = 0;          // loss filter states
        this.P = 100; this.P0 = 100;       // current / un-bent period (samples)
        this.Pt = 100;                     // target period
        this.glide = 0;                    // per-sample step toward Pt
        this.bend = 0;                     // semitones (+ up)
        this.bendT = 0; this.bendRate = 0;
        this.vibDepth = 0; this.vibRate = 5.5; this.vibPh = 0;
        this.tm = 0;                       // tension-modulation cents, decays
        this.tmDecay = 0.999;
        this.harm = 1;                     // virtual-string divisor for harmonics
        this.detune = 1.0004;              // polarisation B period ratio
        this.wA = 0.72; this.wB = 0.28;
        this.smin = 0.3; this.sb = 0.001;   // current damping: sigma(f) = smin + sb*f
        this.smint = 0.3; this.sbt = 0.001; // target damping
        this.dampK = 0.0;                  // smoothing coefficient per block
        this.taps = [];                    // [{d: samples, w: weight}]
        this.tapSum = 1;
        this.lossA = { g: 0.99, c: 0, a: 0, tau: 0 };
        this.lossB = { g: 0.99, c: 0, a: 0, tau: 0 };
        this.xA = 0; this.xB = 0;          // loss filter input history (the zero)
        this.lossDirty = true;
        this.idle = 0;
        this.string = 0;
        this.fret = 0;
        this.f0 = 110;
        this.silentBlocks = 0;
        this.ad = 0;                       // dispersion allpass coefficient (stiffness)
        this.tauD = DISP_M;                // its phase delay at the fundamental
        this.apA = new Float64Array(2 * DISP_M);
        this.apB = new Float64Array(2 * DISP_M);
    }

    Slot.prototype.reset = function () {
        this.a.fill(0); this.b.fill(0); this.h.fill(0);
        this.sA = 0; this.sB = 0; this.xA = 0; this.xB = 0; this.w = 0;
        this.excLen = 0; this.excPos = 0;
        this.bend = 0; this.bendT = 0; this.bendRate = 0;
        this.vibDepth = 0; this.vibPh = 0;
        this.tm = 0; this.harm = 1;
        this.gain = 1; this.gainTarget = 1; this.gainStep = 0;
        this.silentBlocks = 0;
        this.apA.fill(0); this.apB.fill(0);
    };

    /* Scale everything the string holds: a pick landing on a ringing string
       stops part of its motion. */
    Slot.prototype.scale = function (k) {
        var a = this.a, b = this.b, h = this.h;
        for (var i = 0; i < LEN; i++) { a[i] *= k; b[i] *= k; h[i] *= k; }
        this.sA *= k; this.sB *= k; this.xA *= k; this.xB *= k;
        for (var j = 0; j < this.apA.length; j++) { this.apA[j] *= k; this.apB[j] *= k; }
    };

    /* Recompute loop filters for the current period and damping. */
    var _G = [0, 0, 0], _W = [0, 0, 0], _F = [0, 0, 0];
    Slot.prototype.updateLoss = function () {
        var sr = this.sr;
        var P = this.Ploop || (this.P / this.harm);
        var f0 = sr / P;
        var fB = Math.max(f0 * 1.6, 900), fC = Math.max(fB * 1.6, 3000);
        var nyq = sr * 0.45;
        var perSec = P / sr;
        this.tauD = DISP_M * apTau(2 * Math.PI * f0 / sr, this.ad);
        var sm = this.smin, sb = this.sb;
        for (var pass = 0; pass < 2; pass++) {
            // polarisation B is the one that couples less to the bridge: it
            // decays SLOWER and carries the late sustain (two-stage decay)
            var kmin = pass ? 0.85 : 1, kb = pass ? 0.72 : 1;
            var Pp = pass ? perSec * this.detune : perSec;
            _F[0] = pass ? f0 / this.detune : f0; _F[1] = fB; _F[2] = fC;
            for (var i = 0; i < 3; i++) {
                _G[i] = Math.exp(-(sm * kmin + sb * kb * _F[i]) * Pp);
                _W[i] = 2 * Math.PI * _F[i] / sr;
            }
            var L = pass ? this.lossB : this.lossA;
            if (fC >= nyq) {
                var f1 = Math.min(fB, nyq);
                designLoss1(_G[0], Math.exp(-(sm * kmin + sb * kb * f1) * Pp), _W[0], 2 * Math.PI * f1 / sr, L);
            } else designLoss(_G, _W, L);
        }
        this.lossDirty = false;
    };

    /* Render n samples, ADDING into out[off..off+n). Returns peak abs.
       bOut (optional) accumulates the raw string force at the bridge;
       bIn/kIn (optional) drive this slot FROM the bridge - that is how an
       open string that nobody picked starts to ring along (sympathy). */
    Slot.prototype.render = function (out, off, n, bOut, bIn, kIn) {
        var a = this.a, b = this.b, h = this.h;
        var w = this.w;
        var exc = this.exc, excLen = this.excLen, excPos = this.excPos;
        var sA = this.sA, sB = this.sB, xA = this.xA, xB = this.xB;
        var LA = this.lossA, LB = this.lossB;
        var gA = LA.g, cA = LA.c, aA = LA.a, gB = LB.g, cB = LB.c, aB = LB.a;
        var wA = this.wA, wB = this.wB;
        var taps = this.taps, nt = taps.length, tapSum = this.tapSum;
        var gain = this.gain, gT = this.gainTarget, gS = this.gainStep;
        var sr = this.sr;

        // period for this block (glide + bend + vibrato + tension), piecewise
        var P = this.P;
        var harm = this.harm;
        var peak = 0;

        // block-rate modulation: compute start/end periods and lerp
        var Pstart = this.curEff === undefined ? P / harm : this.curEff;
        // advance glide
        if (this.glide !== 0) {
            var nextP0 = this.P0 + this.glide * n;
            if ((this.glide > 0 && nextP0 >= this.Pt) || (this.glide < 0 && nextP0 <= this.Pt)) {
                nextP0 = this.Pt; this.glide = 0;
            }
            this.P0 = nextP0;
            this.lossDirty = true;
        }
        // bend toward target
        if (this.bendRate !== 0) {
            var nb = this.bend + this.bendRate * n;
            if ((this.bendRate > 0 && nb >= this.bendT) || (this.bendRate < 0 && nb <= this.bendT)) {
                nb = this.bendT; this.bendRate = 0;
            }
            this.bend = nb;
            this.lossDirty = true;
        }
        var cents = this.tm;
        if (this.vibDepth > 0) {
            this.vibPh += 2 * Math.PI * this.vibRate * n / sr;
            if (this.vibPh > 6.283185307) this.vibPh -= 6.283185307;
            // guitar vibrato bends UP from the fretted pitch, never below it
            cents += this.vibDepth * 100 * 0.5 * (1 - Math.cos(this.vibPh));
        }
        this.tm *= Math.pow(this.tmDecay, n);
        var ratio = Math.pow(2, (this.bend * 100 + cents) / 1200);
        var Pend = this.P0 / ratio / harm;
        this.P = this.P0 / ratio;
        this.Ploop = Pend;
        if (this.lossDirty || Math.abs(Pend - Pstart) > 0.02) {
            this.updateLoss();
            LA = this.lossA; LB = this.lossB;
            gA = LA.g; cA = LA.c; aA = LA.a; gB = LB.g; cB = LB.c; aB = LB.a;
        }
        this.curEff = Pend;

        // damping smoothing (release / palm mute changes are not instant)
        if (this.smin !== this.smint || this.sb !== this.sbt) {
            var k = this.dampK || 0.25;
            this.smin += (this.smint - this.smin) * k;
            this.sb += (this.sbt - this.sb) * k;
            if (Math.abs(this.smin - this.smint) < 1e-4 * (1 + this.smint)) this.smin = this.smint;
            if (Math.abs(this.sb - this.sbt) < 1e-7 + 1e-4 * this.sbt) this.sb = this.sbt;
            this.lossDirty = true;
        }

        var tauA = LA.tau + this.tauD, tauB = LB.tau + this.tauD;
        var dStartA = Pstart - tauA, dEndA = Pend - tauA;
        var dStartB = Pstart * this.detune - tauB, dEndB = Pend * this.detune - tauB;
        var inv = 1 / n;
        // stiffness: DISP_M allpass sections per polarisation, states in locals
        var ad = this.ad, pA = this.apA, pB = this.apB;
        var a1x = pA[0], a1y = pA[1], a2x = pA[2], a2y = pA[3], a3x = pA[4], a3y = pA[5], a4x = pA[6], a4y = pA[7];
        var b1x = pB[0], b1y = pB[1], b2x = pB[2], b2y = pB[3], b3x = pB[4], b3y = pB[5], b4x = pB[6], b4y = pB[7];
        var q;

        for (var i = 0; i < n; i++) {
            var fr = (i + 1) * inv;
            var DA = dStartA + (dEndA - dStartA) * fr;
            var DB = dStartB + (dEndB - dStartB) * fr;
            if (DA < 2) DA = 2;
            if (DB < 2) DB = 2;

            // ---- polarisation A: 4-point Lagrange read at delay DA
            var kA = DA | 0;
            var dA = DA - kA + 1;            // in [1,2)
            var iA = (w - kA + 1) & MASK;
            var x0 = a[iA], x1 = a[(iA - 1) & MASK], x2 = a[(iA - 2) & MASK], x3 = a[(iA - 3) & MASK];
            var dm1 = dA - 1, dm2 = dA - 2, dm3 = dA - 3;
            var yA = -dm1 * dm2 * dm3 / 6 * x0 + dA * dm2 * dm3 * 0.5 * x1
                     - dA * dm1 * dm3 * 0.5 * x2 + dA * dm1 * dm2 / 6 * x3;
            sA = gA * (yA + cA * xA) - aA * sA;
            xA = yA;
            q = ad * (sA - a1y) + a1x; a1x = sA; a1y = q;
            q = ad * (a1y - a2y) + a2x; a2x = a1y; a2y = q;
            q = ad * (a2y - a3y) + a3x; a3x = a2y; a3y = q;
            q = ad * (a3y - a4y) + a4x; a4x = a3y; a4y = q;

            // ---- polarisation B
            var kB = DB | 0;
            var dB = DB - kB + 1;
            var iB = (w - kB + 1) & MASK;
            var z0 = b[iB], z1 = b[(iB - 1) & MASK], z2 = b[(iB - 2) & MASK], z3 = b[(iB - 3) & MASK];
            var em1 = dB - 1, em2 = dB - 2, em3 = dB - 3;
            var yB = -em1 * em2 * em3 / 6 * z0 + dB * em2 * em3 * 0.5 * z1
                     - dB * em1 * em3 * 0.5 * z2 + dB * em1 * em2 / 6 * z3;
            sB = gB * (yB + cB * xB) - aB * sB;
            xB = yB;
            q = ad * (sB - b1y) + b1x; b1x = sB; b1y = q;
            q = ad * (b1y - b2y) + b2x; b2x = b1y; b2y = q;
            q = ad * (b2y - b3y) + b3x; b3x = b2y; b3y = q;
            q = ad * (b3y - b4y) + b4x; b4x = b3y; b4y = q;

            var e = 0;
            if (excPos < excLen) e = exc[excPos++];
            if (bIn) e += kIn * bIn[off + i];
            var vA = a4y + e * wA;
            var vB = b4y + e * wB;
            a[w] = vA;
            b[w] = vB;
            var y = vA + vB;
            h[w] = y;
            if (bOut) bOut[off + i] += y * gain;

            // ---- pickup comb(s): out = sum wi*(y - y[n - di])
            var o = y * tapSum;
            for (var t = 0; t < nt; t++) {
                var tp = taps[t];
                var dd = tp.d;
                var kd = dd | 0, fd = dd - kd;
                var j0 = (w - kd) & MASK;
                var hv = h[j0] + (h[(j0 - 1) & MASK] - h[j0]) * fd;
                o -= tp.w * hv;
            }

            if (gS !== 0) {
                gain += gS;
                if ((gS < 0 && gain <= gT) || (gS > 0 && gain >= gT)) { gain = gT; gS = 0; }
            }
            o *= gain;
            out[off + i] += o;
            var ao = o < 0 ? -o : o;
            if (ao > peak) peak = ao;
            w = (w + 1) & MASK;
        }

        this.w = w;
        this.excPos = excPos;
        this.sA = sA; this.sB = sB; this.xA = xA; this.xB = xB;
        pA[0] = a1x; pA[1] = a1y; pA[2] = a2x; pA[3] = a2y; pA[4] = a3x; pA[5] = a3y; pA[6] = a4x; pA[7] = a4y;
        pB[0] = b1x; pB[1] = b1y; pB[2] = b2x; pB[3] = b2y; pB[4] = b3x; pB[5] = b3y; pB[6] = b4x; pB[7] = b4y;
        this.gain = gain; this.gainStep = gS;
        if (gain <= 0 && gT <= 0) this.active = false;
        if (peak < 2e-5 && excPos >= excLen) {
            if (++this.silentBlocks > 40) this.active = false;
        } else this.silentBlocks = 0;
        return peak;
    };

    /* Initial tension-modulation glide by string (0 = high e ... 5 = low E,
       6 = a seven-string's low B), open string, medium-hard pick. Fitted so a
       pitch tracker reads the model as it reads EGFxSet notes; the stretched
       partials of a stiff string already read ~8 cents sharp at the attack,
       so this is less than the raw numbers (14-31 cents at 50 ms). */
    var TM_OPEN = [5, 4, 10, 4, 8, 13, 13];

    // ───────────────────────────── guitar ─────────────────────────────────
    function Guitar(sr, opts) {
        opts = opts || {};
        this.sr = sr;
        this.nStrings = 6;
        this.slots = [];
        this.cur = [];                 // index of the current slot per string
        /* Two slots alternate per string (a re-picked string cross-fades);
           the third carries a pinch harmonic's high partial. Seven strings are
           allocated always; a six-string guitar simply never touches the 7th. */
        for (var s = 0; s < 7; s++) {
            this.slots.push([new Slot(sr), new Slot(sr), new Slot(sr), new Slot(sr)]);
            this.cur.push(0);
        }
        /* Sympathy. The strings share a bridge: a played note pushes on it,
           and every open string whose harmonics coincide starts to ring along
           - quietly, but it is part of why a real guitar sounds alive and a
           model of one string at a time sounds like a synth. Measured: the
           non-harmonic residual of real DI notes (EGFxSet) carries half of
           what the AudioSet tagger hears as "guitar" in a sustained note, and
           most of it sits at other strings' harmonics.
           Slot 3 of each string is its sympathetic resonator, driven one block
           late from the bridge sum (a one-way coupling: always stable). */
        this.bridge = new Float32Array(128);
        this.bridgePrev = new Float32Array(128);
        this.sympathy = 0.6;          // 0..1, presets lower it for high gain
        this.muted = [false, false, false, false, false, false, false];
        this.model = 's';
        this.sel = 'b';
        this.tuning = TUNINGS.std.slice();
        this.capo = 0;
        this.pickMm = 80;              // picking distance from the bridge
        this.events = [];              // pending scheduled events, sorted by frame
        this.frame = 0;                // absolute frame counter
        this.rand = rng(opts.seed || 1234567);
        this.dc = { x: 0, y: 0 };
        this.env = 0;
        this.envA = Math.exp(-1 / (0.002 * sr));
        this.envR = Math.exp(-1 / (0.09 * sr));
        this.held = [false, false, false, false, false, false, false];
        this.letRing = false;
        /* A real guitar is never perfectly in tune with itself: each string sits
           a few cents off, and pressing a string onto a fret stretches it a
           little sharp. A perfectly tuned model beats like an organ - the
           AudioSet tagger heard our first power chords as "Synthesizer" until
           these were added (tests/audio, exp1). Seeded, so a take re-renders
           identically. */
        this.stringCents = [];
        for (var sc = 0; sc < 7; sc++) this.stringCents.push(clamp((this.rand() + this.rand() + this.rand() - 1.5) * 3.2, -4.5, 4.5));
        this.intonation = 1;           // 0 = perfect (measurements), 1 = a real guitar
        /* The life under a sustained note. Split real DI notes (EGFxSet) into
           their harmonic part and the rest: the REST alone is what an AudioSet
           tagger still calls "guitar" (0.13), and adding real residual to our
           notes lifted them 0.12 -> 0.20, while pure hiss or a clean attack
           did far less (tests/audio, 2026-09-25). What is in it: pickup hiss,
           mains hum (a single coil is a coil antenna; a humbucker cancels
           it), and string noise that rides on the vibration.
           All three follow the guitar's own activity with a 150 ms release -
           the way a gate opens when you play - so the instrument is silent
           between phrases and never needs a gate of its own. */
        this.noise = 0.6;              // 0 = studio-silent, 1 = a cheap cable in a bad room
        this.humHz = 50;
        this._nz = { b0: 0, b1: 0, b2: 0, hp: 0, hpx: 0, lp: 0, ph: 0, gate: 0, sn: 0 };
        this.level = 0.16;             // DI level: a hard power chord peaks ~0.3, like a real passive guitar into an interface
    }

    Guitar.prototype.setModel = function (key, sel) {
        if (!MODELS[key]) return;
        this.model = key;
        var m = MODELS[key];
        this.sel = sel && m.sel[sel] ? sel : m.def;
    };
    Guitar.prototype.setPickup = function (sel) {
        var m = MODELS[this.model];
        if (m.sel[sel]) this.sel = sel;
        // re-tap every ringing slot so a switch mid-note is heard, as on a guitar
        for (var s = 0; s < this.nStrings; s++) {
            for (var k = 0; k < 4; k++) {
                var sl = this.slots[s][k];
                if (sl.active) this._taps(sl, s);
            }
        }
    };
    Guitar.prototype.setTuning = function (arr) {
        if (arr && arr.length >= 4 && arr.length <= 7) {
            // a string that disappears (7 -> 6) is silenced, not left ringing
            if (arr.length < this.tuning.length) {
                for (var s = arr.length; s < 7; s++) for (var k = 0; k < 3; k++) this.slots[s][k].active = false;
            }
            this.tuning = arr.slice();
            this.nStrings = arr.length;
        }
    };
    Guitar.prototype.setCapo = function (n) { this.capo = clamp(n | 0, 0, 12); };
    Guitar.prototype.setPickPosition = function (mm) { this.pickMm = clamp(mm, 20, 220); };

    /* Wake the sympathetic resonators of the strings that are free to ring. */
    Guitar.prototype._wakeSympathy = function (except, fPlayed) {
        if (this.sympathy <= 0) return;
        for (var s = 0; s < this.nStrings; s++) {
            if (s === except || this.muted[s]) continue;
            var own = this.slots[s][this.cur[s]];
            if (own.active && own.gainTarget > 0) continue;
            var r = this.slots[s][3];
            if (r.active) continue;
            // only a string with a harmonic near one of the played note's can
            // ring along; waking the rest would cost CPU and add nothing
            var fo = this.noteHz(s, 0, 0.5), hit = false;
            for (var ka = 1; ka <= 8 && !hit; ka++) {
                for (var kb = 1; kb <= 8; kb++) {
                    var d = ka * fPlayed - kb * fo;
                    if (d < 0) d = -d;
                    if (d < 0.004 * ka * fPlayed && ka * fPlayed < 4000) { hit = true; break; }
                }
            }
            if (!hit) continue;
            r.reset();
            r.active = true; r.string = s; r.fret = 0;
            var f = this.noteHz(s, 0, 0.5);
            r.f0 = f; r.harm = 1;
            r.P0 = this.sr / f; r.Pt = r.P0; r.P = r.P0; r.glide = 0;
            r.curEff = undefined; r.Ploop = r.P0;
            r.detune = 1.0003;
            r.wA = 0.7; r.wB = 0.3;
            var sig = this._damp(s, 0, 1, 0, false);
            r.smin = r.smint = sig.smin; r.sb = r.sbt = sig.b;
            r.tm = 0;
            r.silentBlocks = -400;    // give it time to build up before idling out
            this._disp(r, s, 0, 1, f);
            this._taps(r, s);
            r.updateLoss();
        }
    };

    /* Stiffness of this string at this fret (B grows as 1/L^2), as the
       loop's allpass coefficient. A harmonic's loop is the string divided by
       n: its partials are every n-th one of the string's, so B scales by n^2. */
    Guitar.prototype._disp = function (sl, s, fret, harm, f) {
        var B = (this.model === 'bass' ? B_BASS[s] : B_OPEN[s]) || 1.6e-4;
        B *= Math.pow(2, 2 * (this.capo + fret) / 12) * harm * harm;
        sl.ad = dispCoef(f * harm, B);
        sl.lossDirty = true;
    };

    Guitar.prototype.openHz = function (s) { return midiToHz(this.tuning[s] + this.capo); };
    /* The pitch a finger actually gets: string offset + fretting sharpness. */
    Guitar.prototype.noteHz = function (s, fret, vel) {
        var cents = 0;
        if (this.intonation > 0) {
            cents = this.stringCents[s] || 0;
            if (fret > 0) cents += (s >= 3 ? 2.6 : 1.8) + 0.11 * fret + (this.rand() - 0.5) * 2.2 + (vel || 0.7) * 1.2;
            cents *= this.intonation;
        }
        return midiToHz(this.tuning[s] + this.capo + fret + cents / 100);
    };
    Guitar.prototype.noteMidi = function (s, fret) { return this.tuning[s] + this.capo + fret; };

    /* Pickup comb taps for a slot on string s. Delay = 2d/c = (d/L)*P_open. */
    Guitar.prototype._taps = function (sl, s) {
        var m = MODELS[this.model];
        var sel = m.sel[this.sel];
        var Popen = this.sr / midiToHz(this.tuning[s] + this.capo);
        // bends raise tension -> faster waves -> shorter physical delays
        var bendRatio = Math.pow(2, sl.bend / 12);
        var taps = [];
        var sum = 0;
        var frac = Math.min(1, s / 5);        // 0 = high E (treble side), 1 = low E
        for (var i = 0; i < sel.c.length; i++) {
            var coil = m.coils[sel.c[i][0]];
            var wgt = sel.c[i][1];
            if (coil.hb) {
                taps.push({ d: (coil.d[0] / m.scale) * Popen / bendRatio, w: wgt * 0.5 });
                taps.push({ d: (coil.d[1] / m.scale) * Popen / bendRatio, w: wgt * 0.5 });
            } else {
                var dmm = coil.d[0] + (coil.d[1] - coil.d[0]) * frac;
                taps.push({ d: (dmm / m.scale) * Popen / bendRatio, w: wgt });
            }
            sum += wgt;
        }
        sl.taps = taps;
        sl.tapSum = sum;
    };

    /* Build the pick excitation into sl.exc. */
    Guitar.prototype._excite = function (sl, s, vel, opts) {
        var sr = this.sr;
        var m = MODELS[this.model];
        var Popen = sr / midiToHz(this.tuning[s] + this.capo);
        var pickMm = opts.pickMm || this.pickMm;
        if (sl.harm > 1) pickMm = Math.min(pickMm, 45);   // harmonics are picked near the bridge
        var Dp = (pickMm / m.scale) * Popen / Math.pow(2, sl.bend / 12);
        Dp *= (0.94 + 0.12 * this.rand());           // the hand is never in one place
        // pick hardness: the string slips off a pick in well under 0.1 ms.
        // Measured on real DI attacks (EGFxSet): the first 10 ms of a real
        // pick carry energy up to 14 kHz that a 0.25 ms edge simply cannot
        // make (-18 dB at 7 kHz against the real note). A palm or a thumb is
        // the soft case.
        var widthMs = opts.soft ? 1.4 : (0.34 - 0.2 * vel);
        var Wp = clamp(Math.round(widthMs * 0.001 * sr), 2, 160);
        // an upstroke pushes the string the other way: the same pulse, inverted
        var amp = vel * (opts.legato ? 0.32 : 1) * STRING_OUT[s] * (opts.up ? -1 : 1);
        var exc = sl.exc;
        /* The push. Inverse-filtering real DI notes (Karjalainen et al.'s
           method on EGFxSet) shows every pick stroke starts with the string
           being DRAGGED for 2.5-6 ms before it slips off: a slow ramp of the
           opposite sign at ~30-45 % of the release, ending exactly at the
           release. It is the low "thock" under a real attack; a model that
           starts at the release sounds like a struck wire. A hammer-on has no
           pick and no push. */
        var Tp = (opts.legato || opts.noPush) ? 0 : Math.round((2.2 + 3.8 * (1 - vel)) * 0.001 * sr * (0.85 + 0.3 * this.rand()));
        var Ap = opts.soft ? 0.18 : 0.38;
        var len = Math.min(exc.length, Tp + Math.ceil(Dp + Wp) + 2);
        for (var i = 0; i < len; i++) {
            var v = 0;
            if (i < Tp) {
                v = -Ap * amp * 0.5 * (1 - Math.cos(Math.PI * i / Tp));
            } else {
                var r = i - Tp;
                var s1 = r < Wp ? 0.5 * (1 - Math.cos(Math.PI * r / Wp)) : 1;
                var j = r - Dp;
                var s2 = j <= 0 ? 0 : (j < Wp ? 0.5 * (1 - Math.cos(Math.PI * j / Wp)) : 1);
                // the drag is released together with the string: its level
                // returns to zero over the same edge
                var rel = Tp ? -Ap * amp * (1 - s1) : 0;
                v = amp * (s1 - s2) + rel;
            }
            exc[i] = v;
        }
        // the pick scraping off the string: a short, bright click (differenced
        // noise), louder on wound strings whose windings rasp
        var nz = (0.05 + (s >= 3 ? 0.035 : 0)) * vel * (opts.soft ? 0.25 : 1);
        var nl = Math.min(len - Tp, Math.round(0.0022 * sr));
        var prev = 0;
        for (var q = 0; q < nl; q++) {
            var wn = this.rand() * 2 - 1;
            exc[Tp + q] += nz * (wn - prev) * Math.pow(1 - q / nl, 2);
            prev = wn;
        }
        sl.excLen = len;
        sl.excPos = 0;
    };

    Guitar.prototype._tension = function (sl, s, fret, vel, pm, harm, dead) {
        var sr = this.sr;
        /* A pick pulls the string sharp and the pitch sinks back as it calms
           down (tension modulation: the stretch grows with the square of the
           swing). Measured on EGFxSet DI notes, Strat, medium-hard pick: 6-20
           cents above the settled pitch 50 ms after the pick, more high on a
           low string, gone with a time constant of ~0.6 s. The first model
           had 4-7 cents gone in 70 ms - and a sustained power chord of it
           through the heavy-metal rig tagged Guitar 0.32 where the same chord
           of real notes tagged 0.53: the strings of a chord sink at different
           rates, and the beating that makes is what a synth never has. */
        var tmc = (this.model === 'bass' ? 10 : (TM_OPEN[s] || 12))
            * Math.min(1.6, (vel / 0.85) * (vel / 0.85))
            * Math.pow(2, 1.4 * Math.min(fret, 12) / 12)
            * (1 - 0.6 * pm) * (harm > 1 ? 0.5 : 1);
        if (dead) tmc = 0;
        sl.tm = Math.max(sl.tm, tmc);
        sl.tmDecay = Math.exp(-1 / (0.7 * Math.pow(2, -Math.min(fret, 12) / 12) * (1 - 0.7 * pm) * sr));

    };

    /*
     * Start a note.
     *   s      string index 0 (high E) .. 5 (low E)
     *   fret   0..24
     *   vel    0..1
     *   o      {pm: palm mute 0..1, dead: muted scratch, harm: n (natural
     *          harmonic divisor), pinch, legato (hammer/pull), pickMm,
     *          up: an upstroke}
     */
    Guitar.prototype.noteOn = function (s, fret, vel, o) {
        o = o || {};
        s = clamp(s | 0, 0, this.nStrings - 1);
        vel = clamp(vel === undefined ? 0.8 : vel, 0.02, 1);
        var sr = this.sr;
        var f = this.noteHz(s, fret, vel);
        var cur = this.slots[s][this.cur[s]];

        if (o.legato && cur.active && !o.harm) {
            // hammer-on / pull-off: same vibrating string, new length
            var newP = sr / f;
            cur.P0 = newP; cur.Pt = newP; cur.glide = 0;
            cur.fret = fret; cur.f0 = f;
            cur.bend = 0; cur.bendT = 0; cur.bendRate = 0;
            this._disp(cur, s, fret, 1, f);
            this._excite(cur, s, vel, { legato: true });
            cur.lossDirty = true;
            this.held[s] = true;
            return;
        }

        /* Re-picking a string that still rings at the same fret: the pick stops
           part of the vibration and adds its own on top - the rest of the
           string keeps moving. Starting a fresh string every time (the old
           one faded out in 6 ms) chopped fast picking into separate notes:
           surf tremolo picking tagged "Buzzer" and "Synthesizer". */
        var pmR = o.pm || 0;
        if (cur.active && cur.gainTarget > 0 && cur.gainStep === 0 && cur.fret === fret && cur.harm === 1
            && !o.harm && !o.pinch && !o.dead && cur.bend === 0 && cur.bendT === 0
            && Math.abs(cur.P0 * f / sr - 1) < 0.003) {
            cur.scale(0.35);
            var sgR = this._damp(s, fret, 1, pmR, false);
            cur.smint = sgR.smin; cur.sbt = sgR.b; cur.dampK = 0.5;
            cur.tm *= 0.35;
            this._tension(cur, s, fret, vel, pmR, 1, false);
            this._excite(cur, s, vel * (pmR > 0 ? (1 - 0.25 * pmR) : 1), { pickMm: o.pickMm || this.pickMm, soft: pmR > 0.6, up: !!o.up });
            this.muted[s] = false;
            this.held[s] = true;
            return;
        }

        // the string is now played, not resonating
        var res = this.slots[s][3];
        if (res.active) { res.gainTarget = 0; res.gainStep = -res.gain / Math.max(1, Math.round(0.01 * sr)); }
        this.muted[s] = false;
        // re-picking: the pick touches the ringing string - fade the old slot
        if (cur.active) {
            cur.gainTarget = 0;
            cur.gainStep = -cur.gain / Math.max(1, Math.round(0.006 * sr));
        }
        var k = 1 - this.cur[s];
        this.cur[s] = k;
        var sl = this.slots[s][k];
        sl.reset();
        sl.active = true;
        sl.string = s;
        sl.fret = fret;
        sl.f0 = f;
        var harm = o.harm && o.harm > 1 ? o.harm : 1;
        sl.harm = harm;
        sl.P0 = sr / f; sl.Pt = sl.P0; sl.P = sl.P0; sl.glide = 0;
        sl.curEff = undefined; sl.Ploop = sl.P0 / harm;
        this._disp(sl, s, fret, harm, f);
        sl.detune = 1 + (0.0002 + 0.0005 * this.rand());
        sl.wA = 0.7 + 0.1 * this.rand(); sl.wB = 1 - sl.wA;

        // damping: the measured string, a little more for every fret
        var sig = this._damp(s, fret, harm, o.pm || 0, !!o.dead);
        var pm = o.pm || 0;
        sl.smin = sl.smint = sig.smin;
        sl.sb = sl.sbt = sig.b;
        sl.dampK = 0.25;

        this._tension(sl, s, fret, vel, pm, harm, !!o.dead);

        this._taps(sl, s);
        var pickMm = o.pickMm || this.pickMm;
        this._excite(sl, s, vel * (pm > 0 ? (1 - 0.25 * pm) : 1), { pickMm: pickMm, soft: pm > 0.6, up: !!o.up });
        sl.updateLoss();

        if (o.pinch) {
            // pinch harmonic: the thumb kills the string at a node right after
            // the pick - a high partial survives and the fundamental barely does
            var other = this.slots[s][2];
            other.reset();
            other.active = true;
            other.string = s; other.fret = fret; other.f0 = f;
            var n = clamp(Math.round(1900 / f), 3, 6);
            other.harm = n;
            other.P0 = sr / f; other.Pt = other.P0; other.P = other.P0; other.glide = 0;
            other.curEff = undefined; other.Ploop = other.P0 / n;
            this._disp(other, s, fret, n, f);
            var sg = this._damp(s, fret, n, 0, false);
            other.smin = other.smint = sg.smin * 1.1; other.sb = other.sbt = sg.b * 0.8;
            other.tm = 0;
            this._taps(other, s);
            this._excite(other, s, vel * 1.25, { pickMm: Math.min(pickMm, 45) });
            other.updateLoss();
            // the normal note underneath is quieter
            sl.gain = 0.28; sl.gainTarget = 0.28; sl.gainStep = 0;
        }
        this.held[s] = true;
        if (!o.dead && !(o.pm > 0.5)) this._wakeSympathy(s, f);
    };

    /* Release: the fretting finger lifts; the string stops in ~0.1 s. */
    Guitar.prototype.noteOff = function (s, relSec) {
        s = clamp(s | 0, 0, 6);
        this.held[s] = false;
        if (this.letRing) return;
        var rel = relSec || 0.09;
        for (var k = 0; k < 3; k++) {
            var sl = this.slots[s][k];
            if (!sl.active) continue;
            // finger lifts: the fundamental stops in ~rel s, the top end faster
            sl.smint = 6.91 / rel; sl.sbt = 0.02; sl.dampK = 0.35;
        }
    };

    /* Palm or hand on all strings (stop / choke). */
    Guitar.prototype.muteAll = function () {
        for (var s = 0; s < 7; s++) {
            this.held[s] = false;
            for (var k = 0; k < 4; k++) {
                var sl = this.slots[s][k];
                if (!sl.active) continue;
                sl.smint = 115; sl.sbt = 0.03; sl.dampK = 0.5;
            }
        }
    };

    /* Bend string s to `semis` above the fretted note over `sec`. */
    Guitar.prototype.bend = function (s, semis, sec) {
        s = clamp(s | 0, 0, 6);
        var sl = this.slots[s][this.cur[s]];
        if (!sl.active) return;
        var n = Math.max(1, Math.round((sec || 0.12) * this.sr));
        sl.bendT = clamp(semis, -2, 3);
        sl.bendRate = (sl.bendT - sl.bend) / n;
        if (sl.bendRate === 0) sl.bend = sl.bendT;
        this._taps(sl, s);
    };

    Guitar.prototype.vibrato = function (s, depthSemis, rate) {
        s = clamp(s | 0, 0, 6);
        for (var k = 0; k < 3; k++) {
            var sl = this.slots[s][k];
            sl.vibDepth = clamp(depthSemis || 0, 0, 1);
            if (rate) sl.vibRate = clamp(rate, 2, 9);
        }
    };

    /* Slide the current note to `toFret`: the fret wires make it a fast run of
       semitones, not a glissando - that is how a slide sounds on a fretted
       neck. `sec` is the whole slide. */
    Guitar.prototype.slide = function (s, toFret, sec) {
        s = clamp(s | 0, 0, 6);
        var sl = this.slots[s][this.cur[s]];
        if (!sl.active) return false;
        var from = sl.fret;
        var steps = toFret - from;
        if (!steps) return true;
        var dur = Math.max(0.02, sec || Math.min(0.35, 0.03 * Math.abs(steps)));
        var stepFrames = Math.max(1, Math.round(dur * this.sr / Math.abs(steps)));
        var dir = steps > 0 ? 1 : -1;
        for (var i = 1; i <= Math.abs(steps); i++) {
            this.events.push({ f: this.frame + i * stepFrames, type: '_fret', s: s, fret: from + dir * i });
        }
        this.events.sort(byFrame);
        return true;
    };

    Guitar.prototype._setFret = function (s, fret) {
        var sl = this.slots[s][this.cur[s]];
        if (!sl.active) return;
        var f = this.noteHz(s, fret, 0.6);
        var newP = this.sr / f;
        var n = Math.max(1, Math.round(0.003 * this.sr));
        sl.Pt = newP;
        sl.glide = (newP - sl.P0) / n;
        sl.fret = fret; sl.f0 = f;
        this._disp(sl, s, fret, sl.harm || 1, f);
    };

    function byFrame(a, b) { return a.f - b.f; }

    /*
     * Damping of a note: sigma(f) = smin + b*f. Palm muting is measured
     * behaviour too: the palm on the saddles kills the fundamental in a few
     * tenths of a second and the top end in a few hundredths (the chug).
     */
    Guitar.prototype._damp = function (s, fret, harm, pm, dead) {
        var md = MODELS[this.model].damp;
        var d = md ? md[Math.min(s, md.length - 1)] : DAMP[Math.min(s, 6)];
        var k = 1 + FRET_LOSS * fret;
        var smin = d.smin * k * (fret === 0 ? d.open : 1);
        // an open string has no finger on it: its low partials ring several
        // times longer (s6 150-300 Hz: sigma 0.16 open vs 0.82 fretted)
        var b = d.b * k * (fret === 0 ? (s >= 3 ? 0.6 : 0.85) : 1);
        if (harm > 1) smin *= 1.25;
        if (pm > 0) {
            smin = smin * (1 - pm) + 19 * pm;
            b = b * (1 - pm) + 0.045 * pm;
        }
        if (dead) { smin = 150; b = 0.08; }
        return { smin: smin, b: b };
    };

    /*
     * Queue an event at absolute frame `f` (or now if f is falsy/past).
     * type: on | off | bend | vib | slide | muteAll | pickup | ring
     */
    Guitar.prototype.schedule = function (ev) {
        if (!ev.f || ev.f < this.frame) ev.f = this.frame;
        this.events.push(ev);
        if (this.events.length > 1 && this.events[this.events.length - 2].f > ev.f) this.events.sort(byFrame);
    };

    Guitar.prototype._apply = function (ev) {
        switch (ev.type) {
            case 'on': this.noteOn(ev.s, ev.fret, ev.vel, ev.o); break;
            case 'off': this.noteOff(ev.s, ev.rel); break;
            case 'bend': this.bend(ev.s, ev.semis, ev.sec); break;
            case 'vib': this.vibrato(ev.s, ev.depth, ev.rate); break;
            case 'slide': this.slide(ev.s, ev.fret, ev.sec); break;
            case '_fret': this._setFret(ev.s, ev.fret); break;
            case 'muteAll': this.muteAll(); break;
            case 'pickup': this.setPickup(ev.sel); break;
            case 'model': this.setModel(ev.model, ev.sel); break;
            case 'tuning': this.setTuning(ev.tuning); break;
            case 'capo': this.setCapo(ev.capo); break;
            case 'ring': this.letRing = !!ev.on; break;
            case 'pick': this.setPickPosition(ev.mm); break;
            case 'intonation': this.intonation = clamp(+ev.v || 0, 0, 1.5); break;
            case 'sympathy': this.sympathy = clamp(+ev.v || 0, 0, 1); break;
            case 'noise': this.noise = clamp(+ev.v || 0, 0, 1.5); if (ev.hz) this.humHz = ev.hz === 60 ? 60 : 50; break;
            case 'panic':
                for (var s = 0; s < 7; s++) for (var k = 0; k < 4; k++) { this.slots[s][k].active = false; this.slots[s][k].reset(); }
                this.events.length = 0;
                break;
            case 'cancel':
                // drop what is still queued from one source (a riff's notes on Stop);
                // in place - process() is walking this array
                for (var ci = this.events.length - 1; ci >= 0; ci--) if (this.events[ci].src === ev.src) this.events.splice(ci, 1);
                break;
        }
    };

    /*
     * Render n frames into out (overwrites). Optional env array receives the
     * DI envelope (for the auto-wah and the meters). Events are applied at
     * their exact frame - a riff played by the scheduler is sample-accurate.
     */
    Guitar.prototype.process = function (out, n, env) {
        for (var i = 0; i < n; i++) out[i] = 0;
        if (this.bridge.length < n) { this.bridge = new Float32Array(n); this.bridgePrev = new Float32Array(n); }
        var bIn = this.bridgePrev, bOut = this.bridge;
        for (var bi = 0; bi < n; bi++) bOut[bi] = 0;
        var pos = 0;
        var evs = this.events;
        while (pos < n) {
            // apply all events due at this position
            while (evs.length && evs[0].f <= this.frame + pos) this._apply(evs.shift());
            var next = n;
            if (evs.length) {
                var due = evs[0].f - this.frame;
                if (due < next) next = Math.max(pos + 1, due);
            }
            var len = next - pos;
            var kIn = this.sympathy * 0.0022;
            for (var s = 0; s < 7; s++) {
                var row = this.slots[s];
                for (var k = 0; k < 3; k++) {
                    if (row[k].active) row[k].render(out, pos, len, bOut, null, 0);
                }
                if (row[3].active) row[3].render(out, pos, len, null, bIn, kIn);
            }
            pos = next;
        }
        this.frame += n;
        // the bridge force of this block drives the resonators in the next one
        this.bridgePrev = bOut; this.bridge = bIn;

        // DC blocker (~12 Hz), level, and the life underneath (see constructor)
        var R = 1 - (2 * Math.PI * 12 / this.sr);
        var x1 = this.dc.x, y1 = this.dc.y;
        var lv = this.level;
        var e = this.env, eA = this.envA, eR = this.envR;
        var nzOn = this.noise > 0;
        var M = MODELS[this.model];
        var humK = (M && M.hum !== undefined ? M.hum : 1) * this.noise;
        var nz = this._nz, rnd = this.rand;
        var sr = this.sr;
        var gA = Math.exp(-1 / (0.004 * sr)), gR = Math.exp(-1 / (0.15 * sr));
        var hpK = Math.exp(-2 * Math.PI * 300 / sr), lpK = 1 - Math.exp(-2 * Math.PI * 6500 / sr);
        var dph = 2 * Math.PI * this.humHz / sr;
        var hissA = 7e-5 * this.noise;                       // ~ -78 dB under a typical note (peak ~0.17)
        var humA = 1.7e-4 * humK;                            // ~ -62 dB under a typical note (single coil)
        var snA = 0.0065 * this.noise;                       // string noise ~ -44 dB under the note
        for (var j = 0; j < n; j++) {
            var x = out[j] * lv;
            var y = x - x1 + R * y1;
            x1 = x; y1 = y;
            var ay = y < 0 ? -y : y;
            e = ay > e ? ay + (e - ay) * eA : ay + (e - ay) * eR;
            if (nzOn) {
                var act = e > 2e-4 ? 1 : e * 5000;
                nz.gate = act > nz.gate ? act + (nz.gate - act) * gA : act + (nz.gate - act) * gR;
                if (nz.gate > 1e-4) {
                    // pink-ish noise (Kellet's economy filter) for the string, white for hiss
                    var wn = rnd() * 2 - 1;
                    nz.b0 = 0.99765 * nz.b0 + wn * 0.0990460;
                    nz.b1 = 0.96300 * nz.b1 + wn * 0.2965164;
                    nz.b2 = 0.57000 * nz.b2 + wn * 1.0526913;
                    var pink = (nz.b0 + nz.b1 + nz.b2 + wn * 0.1848) * 0.18;
                    // hiss: white, high-passed at 300 Hz, low-passed at 6.5 kHz (the pickup)
                    var hp = wn - nz.hpx + hpK * nz.hp; nz.hpx = wn; nz.hp = hp;
                    nz.lp += (hp - nz.lp) * lpK;
                    nz.ph += dph; if (nz.ph > 6.283185307) nz.ph -= 6.283185307;
                    var hum = Math.sin(nz.ph) + 0.45 * Math.sin(3 * nz.ph + 0.7) + 0.25 * Math.sin(5 * nz.ph + 1.9);
                    y += nz.gate * (hissA * nz.lp + humA * hum) + pink * snA * e;
                }
            }
            out[j] = y;
            if (env) env[j] = e;
        }
        this.dc.x = x1; this.dc.y = y1; this.env = e;
    };

    /* True while anything is still sounding (the worklet can idle). */
    Guitar.prototype.busy = function () {
        if (this.events.length) return true;
        for (var s = 0; s < 7; s++) for (var k = 0; k < 4; k++) if (this.slots[s][k].active) return true;
        return false;
    };

    /*
     * Offline render of a list of timed events (seconds). Used by the export
     * (re-render at full quality, any tone) and by the measurements.
     */
    /* The same render in slices of ~12 ms with the thread given back between
       them: a 60 s take of chords is 3.2 s of string physics on a desktop, and
       in one task that froze the page - on a phone long enough for "page not
       responding" or an iOS reload, while the "Rendering..." note never got
       painted. Same samples as renderEvents (same generator, same order). */
    function renderEventsAsync(list, sr, seconds, opts, onProgress) {
        var job = renderJob(list, sr, seconds, opts);
        var yieldNow = typeof MessageChannel !== 'undefined'
            // the ports are closed after use: an open one keeps a Node test process (and its memory) alive
            ? function (f) { var ch = new MessageChannel(); ch.port1.onmessage = function () { ch.port1.close(); ch.port2.close(); f(); }; ch.port2.postMessage(0); }
            : function (f) { setTimeout(f, 0); };
        var clock = typeof performance !== 'undefined' && performance.now ? function () { return performance.now(); } : function () { return Date.now(); };
        return new Promise(function (resolve, reject) {
            function slice() {
                try {
                    var t0 = clock();
                    while (!job.done() && clock() - t0 < 12) job.step(64);
                    if (onProgress) onProgress(job.progress());
                    if (job.done()) resolve(job.result()); else yieldNow(slice);
                } catch (e) { reject(e); }
            }
            slice();
        });
    }
    function renderEvents(list, sr, seconds, opts) {
        var job = renderJob(list, sr, seconds, opts);
        while (!job.done()) job.step(1 << 30);
        return job.result();
    }
    function renderJob(list, sr, seconds, opts) {
        opts = opts || {};
        var g = new Guitar(sr, { seed: opts.seed || 7 });
        if (opts.model) g.setModel(opts.model, opts.sel);
        if (opts.tuning) g.setTuning(opts.tuning);
        if (opts.capo) g.setCapo(opts.capo);
        if (opts.pickMm) g.setPickPosition(opts.pickMm);
        if (opts.intonation !== undefined) g.intonation = opts.intonation;
        if (opts.sympathy !== undefined) g.sympathy = opts.sympathy;
        if (opts.noise !== undefined) g.noise = opts.noise;
        var total = Math.ceil(seconds * sr);
        var out = new Float32Array(total);
        var evs = list.slice().sort(function (a, b) { return a.t - b.t; });
        for (var i = 0; i < evs.length; i++) {
            var ev = Object.assign({}, evs[i]);
            ev.f = Math.round(ev.t * sr);
            delete ev.t;
            g.events.push(ev);
        }
        g.events.sort(byFrame);
        var BLK = 128;
        var buf = new Float32Array(BLK);
        var envOut = opts.withEnv ? new Float32Array(total) : null;
        var envBuf = opts.withEnv ? new Float32Array(BLK) : null;
        var p = 0;
        return {
            done: function () { return p >= total; },
            progress: function () { return total ? Math.min(1, p / total) : 1; },
            // `blocks` blocks of 128 frames
            step: function (blocks) {
                for (var k = 0; k < blocks && p < total; k++, p += BLK) {
                    var n = Math.min(BLK, total - p);
                    g.process(buf, n, envBuf);
                    out.set(n === BLK ? buf : buf.subarray(0, n), p);
                    if (envOut) envOut.set(n === BLK ? envBuf : envBuf.subarray(0, n), p);
                }
            },
            result: function () { return envOut ? { di: out, env: envOut } : out; }
        };
    }

    return {
        VERSION: 1,
        MODELS: MODELS,
        TUNINGS: TUNINGS,
        DAMP: DAMP,
        FRET_LOSS: FRET_LOSS,
        Guitar: Guitar,
        renderEvents: renderEvents,
        renderEventsAsync: renderEventsAsync,
        midiToHz: midiToHz,
        designLoss: designLoss
    };
}));
